import { Button } from '@mui/material';
import { useOrganization } from '../hooks/useOrganization';
import { useState, useEffect } from 'react';
import CreateContract from '../components/Layout/CreateContractModal';
import LocalContracts from '../components/Contract/LocalContracts';
import GlobalContracts from '../components/Contract/GlobalContracts';
import { useAuth } from '../hooks/useAuth';

const Contract = () => {
  const { user } = useAuth();
  const { accountSelected, getAccountsList } = useOrganization();
  const isContractUser = user?.roles?.includes('contract');
  const [showCreateContract, setShowCreateContract] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateSortDirection, setDateSortDirection] = useState(null);
  const [selectedAccountName, setSelectedAccountName] = useState('');
  const [activeContractTab, setActiveContractTab] = useState('active');

  useEffect(() => {
    if (accountSelected) {
      const selectedAccount = getAccountsList.find((account) => account._id === accountSelected);
      setSelectedAccountName(selectedAccount?.name || '');
    } else {
      setSelectedAccountName('');
      setSearchTerm('');
    }
  }, [accountSelected, getAccountsList]);

  const handleCreateContract = () => {
    setShowCreateContract(true);
  };

  const createContractClose = () => {
    setShowCreateContract(false);
  };

  const handleAddContract = (contract) => {
    setContracts((currentContracts) => [
      { ...contract, accountId: accountSelected },
      ...currentContracts,
    ]);
  };

  const handleApplyGlobalContract = (globalContract, applicationDetails) => {
    const patientId = applicationDetails.patientInfo.id;
    const patientDate = applicationDetails.patientInfo.date;
    const globalContractId = globalContract.id || globalContract._id || [
      globalContract.practiceName,
      globalContract.providerName,
      globalContract.code,
      globalContract.grossBundle,
    ].join('|');

    setContracts((currentContracts) => {
      const isAlreadyActive = currentContracts.some((contract) => (
        contract.accountId === accountSelected
        && contract.sourceGlobalContractId === globalContractId
        && contract.memberId === patientId
        && contract.date === patientDate
      ));

      if (isAlreadyActive) {
        return currentContracts;
      }

      return [
        {
          id: `active-${globalContractId}-${patientId}-${patientDate}`,
          memberId: patientId,
          date: patientDate,
          rate: globalContract.grossBundle,
          accountId: accountSelected,
          sourceGlobalContractId: globalContractId,
          applicationDetails,
        },
        ...currentContracts,
      ];
    });
    setSearchTerm('');
    setDateSortDirection(null);
    setActiveContractTab('active');
  };

  const handleDateSort = () => {
    setDateSortDirection((currentDirection) => {
      if (currentDirection === null) {
        return 'asc';
      }

      if (currentDirection === 'asc') {
        return 'desc';
      }

      return null;
    });
  };

  const formatContractDate = (date) => {
    if (!date) {
      return '';
    }

    const [year, month, day] = date.split('-');
    if (!year || !month || !day) {
      return date;
    }

    return `${month}/${day}/${year}`;
  };

  const visibleContracts = contracts
    .filter((contract) => {
      if (!accountSelected && !isContractUser) {
        return false;
      }

      const normalizedSearch = searchTerm.trim().toLowerCase();
      if (normalizedSearch === '') {
        return true;
      }

      return [
        contract.memberId,
        contract.date,
        String(contract.rate),
      ].some((value) => value.toLowerCase().includes(normalizedSearch));
    })
    .filter((contract) => contract.accountId === accountSelected)
    .sort((firstContract, secondContract) => {
      if (!dateSortDirection) {
        return 0;
      }

      const firstDate = new Date(firstContract.date).getTime();
      const secondDate = new Date(secondContract.date).getTime();

      return dateSortDirection === 'asc' ? firstDate - secondDate : secondDate - firstDate;
    });
  return (
    <>
      {accountSelected || isContractUser ? (
        <div className="main-content page-dashboard" style={{ padding: '20px' }}>
          <div className="bubble-tabs contract-tabs" role="tablist" aria-label="Contract views">
            <Button
              variant="text"
              onClick={() => setActiveContractTab('active')}
              role="tab"
              aria-selected={activeContractTab === 'active'}
              className={`bubble-tab${activeContractTab === 'active' ? ' bubble-tab--active' : ''}`}
            >
              Active
            </Button>
            <Button
              variant="text"
              onClick={() => setActiveContractTab('global')}
              role="tab"
              aria-selected={activeContractTab === 'global'}
              className={`bubble-tab${activeContractTab === 'global' ? ' bubble-tab--active' : ''}`}
            >
              Global
            </Button>
          </div>

          {activeContractTab === 'active' && (
            <LocalContracts
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              handleCreateContract={handleCreateContract}
              selectedAccountName={selectedAccountName}
              visibleContracts={visibleContracts}
              handleDateSort={handleDateSort}
              dateSortDirection={dateSortDirection}
              formatContractDate={formatContractDate}
              showAccountSubtitle={!isContractUser}
            />
          )}

          {activeContractTab === 'global' && (
            <GlobalContracts
              onApplyContract={handleApplyGlobalContract}
            />
          )}
        </div>
      ) : (
        <div className="main-content page-dashboard">
          <p style={{ textAlign: 'center', marginTop: 50, fontSize: '18px', fontWeight: 'bold' }}>Please select an account to view the contracts.</p>
        </div>
      )}
      {showCreateContract && <CreateContract invite="organization" onClose={createContractClose} onCreate={handleAddContract} />}
    </>
  );
};

export default Contract; 
